import { memo, useState } from 'react';
import { User, Bot, Copy, Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { parseMessageContent, stripThinkingContent, type MessageSegment } from '@/utils/parseMessageContent';
import { ThinkingBlock } from './ThinkingBlock';
import { CodeBlockWithHeader } from './CodeBlockWithHeader';
import type { Message } from '@/types';

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
  showThinking?: boolean;
  thinkingDefaultExpanded?: boolean;
}

export const ChatMessage = memo(function ChatMessage({
  message,
  isStreaming = false,
  showThinking = true,
  thinkingDefaultExpanded = false,
}: ChatMessageProps) {
  const isUser = message.role === 'User';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const textToCopy = showThinking ? message.content : stripThinkingContent(message.content);
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
          isUser
            ? 'bg-primary-600 text-white'
            : 'bg-gradient-to-br from-spark-400 to-spark-600 text-white'
        )}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      {/* Message Content */}
      <div
        className={cn(
          'flex-1 max-w-[80%]',
          isUser ? 'text-right' : 'text-left'
        )}
      >
        <div
          className={cn(
            'inline-block px-4 py-3 rounded-2xl',
            isUser
              ? 'bg-primary-600 text-white rounded-tr-sm'
              : 'bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white rounded-tl-sm'
          )}
        >
          <MessageContent
            content={message.content}
            isStreaming={isStreaming}
            showThinking={showThinking}
            thinkingDefaultExpanded={thinkingDefaultExpanded}
          />
        </div>

        {/* Actions */}
        {!isUser && !isStreaming && (
          <div className="mt-1 flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Copy message"
            >
              {copied ? (
                <Check className="w-3 h-3 text-green-500" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
            <span className="text-xs text-gray-400">
              {message.createdAt ? new Date(message.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }) : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

function MessageContent({
  content,
  isStreaming,
  showThinking,
  thinkingDefaultExpanded,
}: {
  content: string;
  isStreaming: boolean;
  showThinking: boolean;
  thinkingDefaultExpanded: boolean;
}) {
  const segments = parseMessageContent(content);

  const renderSegment = (segment: MessageSegment, index: number) => {
    switch (segment.type) {
      case 'thinking':
        if (!showThinking) return null;
        return (
          <ThinkingBlock
            key={index}
            content={segment.content}
            isComplete={segment.isComplete}
            defaultExpanded={thinkingDefaultExpanded}
            className="my-2"
          />
        );

      case 'codeWithAction':
        return (
          <CodeBlockWithHeader
            key={index}
            action={segment.action}
            filePath={segment.filePath}
            language={segment.language}
            content={segment.content}
            defaultExpanded={false}
            className="my-2"
          />
        );

      case 'code':
        return (
          <CodeBlock
            key={index}
            code={segment.content}
            language={segment.language}
          />
        );

      case 'text':
        return <TextContent key={index} content={segment.content} />;

      default:
        return null;
    }
  };

  return (
    <div className="text-sm break-words">
      {segments.map(renderSegment)}
      {isStreaming && (
        <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />
      )}
    </div>
  );
}

function TextContent({ content }: { content: string }) {
  // Handle inline code
  const inlineParts = content.split(/(`[^`]+`)/g);

  return (
    <span className="whitespace-pre-wrap">
      {inlineParts.map((inlinePart, inlineIndex) => {
        if (inlinePart.startsWith('`') && inlinePart.endsWith('`')) {
          return (
            <code
              key={inlineIndex}
              className="px-1.5 py-0.5 bg-gray-200 dark:bg-dark-600 rounded text-sm font-mono"
            >
              {inlinePart.slice(1, -1)}
            </code>
          );
        }
        // Handle bold text
        return inlinePart.split(/(\*\*[^*]+\*\*)/g).map((boldPart, boldIndex) => {
          if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
            return (
              <strong key={`${inlineIndex}-${boldIndex}`}>{boldPart.slice(2, -2)}</strong>
            );
          }
          return boldPart;
        });
      })}
    </span>
  );
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-2 rounded-lg overflow-hidden bg-gray-900 dark:bg-dark-900">
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800 dark:bg-dark-800">
        <span className="text-xs text-gray-400">{language}</span>
        <button
          onClick={handleCopy}
          className="p-1 text-gray-400 hover:text-white transition-colors"
        >
          {copied ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>
      </div>
      <pre className="p-3 overflow-x-auto">
        <code className="text-xs font-mono text-gray-100">{code}</code>
      </pre>
    </div>
  );
}
